import React from 'react';

import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { closeRemixModal } from './editor-commands/close-remix-modal';
import { openRemixModal } from './editor-commands/open-remix-modal';
import { replaceMauiApp } from './editor-commands/replace-maui-app';
import { startRemixThinking } from './editor-commands/start-remix-thinking';
import { stopRemixThinking } from './editor-commands/stop-remix-thinking';
import type { MalleableUiPlugin, MalleableUiSharedState } from './malleableUiPluginType';
import { remixButtonDecoration } from './pm-plugins/decorations-remix-button';
import {
	createMauiDiagramPastePmPlugin,
	mauiDiagramPastePluginKey,
} from './pm-plugins/maui-diagram-paste-plugin';
import { createMalleableUiPmPlugin, malleableUiPluginKey } from './pm-plugins/plugin-key';
import {
	createRemixThinkingPmPlugin,
	remixThinkingPluginKey,
} from './pm-plugins/remix-thinking-plugin';
import { MauiDiagramPasteFlag } from './ui/components/MauiDiagramPasteFlag';
import { PersistedSelectionHighlightStyles } from './ui/components/PersistedSelectionHighlightStyles';
import { RemixContent } from './ui/components/RemixContent';
import { shouldShowRemixForSelectionContext } from './ui/remix-config';
import { getOverriddenFloatingToolbarConfig } from './ui/toolbars/overridden-floating-toolbar-config';
import { getSelectionToolbarComponents } from './ui/toolbars/toolbar-components';

const REMIX_BUTTON_DECORATION_TYPE = 'REMIX_BUTTON';

export const malleableUiPlugin: MalleableUiPlugin = ({ api, config }) => {
	if (api && expValEquals('confluence_remix_icon_right_side', 'isEnabled', true)) {
		api.toolbar?.actions.registerComponents(getSelectionToolbarComponents(api));
		api.blockControls.actions.registerNodeDecoration({
			type: REMIX_BUTTON_DECORATION_TYPE,
			create: (params) => remixButtonDecoration({ ...params, api }),
			showInViewMode: true,
			shouldCreate: (params) => {
				const { editorState, rootPos } = params;
				if (rootPos === undefined) {
					return false;
				}
				const node = editorState.doc.nodeAt(rootPos);
				return shouldShowRemixForSelectionContext({ node: node ?? undefined });
			},
		});
	}

	return {
		name: 'malleableUi',

		pmPlugins() {
			const plugins = [
				{
					name: 'malleableUi',
					plugin: ({ dispatch }: { dispatch: Dispatch }) => createMalleableUiPmPlugin(dispatch),
				},
				{
					name: 'remixThinking',
					plugin: ({ dispatch }: { dispatch: Dispatch }) => createRemixThinkingPmPlugin(dispatch),
				},
			];

			if (config?.diagramPaste) {
				plugins.push({
					name: 'mauiDiagramPaste',
					plugin: ({ dispatch }: { dispatch: Dispatch }) =>
						createMauiDiagramPastePmPlugin(dispatch, config.diagramPaste!),
				});
			}

			return plugins;
		},

		getSharedState(editorState): MalleableUiSharedState | undefined {
			if (!editorState) {
				return undefined;
			}
			const malleableUiState = malleableUiPluginKey.getState(editorState);
			const remixThinkingState = remixThinkingPluginKey.getState(editorState);
			if (!malleableUiState || !remixThinkingState) {
				return undefined;
			}
			return {
				isRemixModalOpen: malleableUiState.isRemixModalOpen,
				isThinking: remixThinkingState.isThinking,
				diagramPaste: mauiDiagramPastePluginKey.getState(editorState),
			};
		},

		commands: {
			openRemixModal,
			closeRemixModal,
			startRemixThinking,
			stopRemixThinking,
			replaceMauiApp,
		},

		contentComponent: () => {
			return (
				<>
					<PersistedSelectionHighlightStyles />
					<RemixContent api={api} config={config} />
					{config?.diagramPaste && <MauiDiagramPasteFlag api={api} />}
				</>
			);
		},

		pluginsOptions: {
			floatingToolbar: getOverriddenFloatingToolbarConfig(api),
		},
	};
};
